import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import logo from '../../../assets/images/SVG/eduoneLogo.svg';

const MobileBlocker = () => {
  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 99999,
        background: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        boxSizing: 'border-box',
        overflow: 'hidden',
        fontFamily: "'Poppins', sans-serif"
      }}
    >
      {/* Decorative Blur Spheres */}
      <Box
        sx={{
          position: 'absolute',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(255, 80, 45, 0.08)',
          filter: 'blur(60px)',
          top: '-50px',
          left: '-50px',
          zIndex: 1
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(124, 58, 237, 0.08)',
          filter: 'blur(60px)',
          bottom: '-50px',
          right: '-50px',
          zIndex: 1
        }}
      />

      {/* Main Container Card */}
      <Box
        sx={{
          maxWidth: '440px',
          width: '100%',
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(20px)',
          border: '1.5px solid rgba(255, 255, 255, 0.7)',
          borderRadius: '24px',
          p: { xs: 4, sm: 5 },
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.04)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          boxSizing: 'border-box',
          zIndex: 2,
          animation: 'fadeUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards',
          '@keyframes fadeUp': {
            '0%': { opacity: 0, transform: 'translateY(15px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' }
          }
        }}
      >
        {/* EduOne Logo */}
        <Box sx={{ mb: 4 }}>
          <img src={logo} alt="EduOne" style={{ height: '36px', objectFit: 'contain' }} />
        </Box>

        {/* Headings */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: '20px',
            color: '#1e293b',
            fontFamily: 'Poppins',
            lineHeight: 1.3,
            mb: 1.5
          }}
        >
          Access via Desktop or Mobile App
        </Typography>
        <Typography
          sx={{
            color: '#64748b',
            fontSize: '13px',
            fontFamily: 'Poppins',
            lineHeight: 1.5,
            mb: 4
          }}
        >
          The EduOne Web Portal is optimized for larger screens. To continue learning on your mobile device, please download our official student application.
        </Typography>

        {/* Download Buttons Stack */}
        <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
          {/* App Store Link */}
          <Box
            component="a"
            href="https://apps.apple.com/lk/app/eduone-ai-learning-platform/id6756294427"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              bgcolor: '#000000',
              color: '#ffffff',
              py: 1.25,
              borderRadius: '12px',
              textDecoration: 'none',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.02)',
                bgcolor: '#1a1a1a',
                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.12)'
              }
            }}
          >
            <svg width="18" height="22" viewBox="0 0 18 22" fill="white">
              <path d="M14.94 11.59C14.96 9.94 15.76 8.39 17.1 7.44C16.24 6.2 14.86 5.42 13.38 5.32C11.82 5.16 10.31 6.26 9.52 6.26C8.71 6.26 7.49 5.34 6.18 5.37C4.43 5.43 2.84 6.43 2 7.97C0.19 11.13 1.54 15.82 3.27 18.38C4.14 19.63 5.15 21.04 6.48 20.99C7.77 20.93 8.26 20.15 9.79 20.15C11.3 20.15 11.75 20.99 13.1 20.96C14.49 20.93 15.37 19.69 16.2 18.43C16.82 17.54 17.29 16.56 17.59 15.53C15.93 14.83 14.94 13.27 14.94 11.59Z" />
              <path d="M12.26 3.68C13.01 2.78 13.38 1.63 13.29 0.47C12.15 0.59 11.1 1.14 10.34 2.01C9.6 2.86 9.21 3.98 9.29 5.1C10.43 5.11 11.5 4.58 12.26 3.68Z" />
            </svg>
            <Box sx={{ textAlign: 'left' }}>
              <Typography sx={{ fontSize: '8px', lineHeight: 1.1, fontFamily: 'Poppins', color: 'rgba(255, 255, 255, 0.7)' }}>
                Download on the
              </Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, lineHeight: 1.1, fontFamily: 'Poppins', color: '#ffffff' }}>
                App Store
              </Typography>
            </Box>
          </Box>

          {/* Google Play Link */}
          <Box
            component="a"
            href="https://play.google.com/store/apps/details?id=com.fonixedu.eduone&hl=en"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              bgcolor: '#000000',
              color: '#ffffff',
              py: 1.25,
              borderRadius: '12px',
              textDecoration: 'none',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.02)',
                bgcolor: '#1a1a1a',
                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.12)'
              }
            }}
          >
            <svg width="20" height="22" viewBox="0 0 20 22" fill="none">
              <path d="M0.35 0.65C0.13 0.89 0 1.24 0 1.68V20.32C0 20.76 0.13 21.11 0.35 21.35L0.42 21.42L11.29 10.55V10.45L0.42 -0.42L0.35 0.65Z" fill="#4285F4" />
              <path d="M14.91 14.17L11.29 10.55V10.45L14.91 6.83L14.99 6.88L19.27 9.27C20.47 9.94 20.47 11.06 19.27 11.74L14.99 14.12L14.91 14.17Z" fill="#FBBC04" />
              <path d="M14.99 14.12L11.29 10.5L0.35 21.35C0.78 21.81 1.5 21.87 2.31 21.42L14.99 14.12Z" fill="#EA4335" />
              <path d="M14.99 6.88L2.31 -0.42C1.5 -0.87 0.78 -0.81 0.35 -0.35L11.29 10.5L14.99 6.88Z" fill="#34A853" />
            </svg>
            <Box sx={{ textAlign: 'left' }}>
              <Typography sx={{ fontSize: '8px', lineHeight: 1.1, fontFamily: 'Poppins', color: 'rgba(255, 255, 255, 0.7)' }}>
                GET IT ON
              </Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, lineHeight: 1.1, fontFamily: 'Poppins', color: '#ffffff' }}>
                Google Play
              </Typography>
            </Box>
          </Box>
        </Stack>

        {/* Footer Brand */}
        <Typography
          sx={{
            color: '#94a3b8',
            fontSize: '11px',
            fontFamily: 'Poppins',
            fontWeight: 500,
            mt: 4
          }}
        >
          FonixEdu © 2026
        </Typography>
      </Box>
    </Box>
  );
};

export default MobileBlocker;
